<template>
	<div>
		<div class="d-flex align-items-center justify-content-between mg-b-25">
			<h3 class="df-title">New Client</h3>
		</div>

		<div v-if="loading">
            <spinner-component :loading="loading"/>
        </div>

        <div v-if="error" class="error">
			{{ error }}
        </div>

		<div class="row" v-if="options">
			<div class="col-12">
				<alert-error :class="'d-block'" :form="form" message="An error occured."></alert-error>
			</div>
            <div class="col-12">
				<form id="newClientWizard" role="application" class="wizard clearfix vertical shadow-sm" @submit.prevent="newClient">
					<div class="steps clearfix">
						<ul role="tablist">
							<li role="tab" class="first" :class="{ 'current': step === 1 }">
								<a id="newClientWizard-t-0" href="#newClientWizard-h-0" @click.prevent>
									<span class="number">1</span>
									<span class="title">Personal Information</span>
								</a>
							</li>
							<li role="tab" class="disabled" :class="{ 'current': step === 2 }">
								<a id="newClientWizard-t-1" href="#newClientWizard-h-1" @click.prevent>
									<span class="number">2</span>
									<span class="title">Identification</span>
								</a>
							</li>
							<li role="tab" class="disabled" :class="{ 'current': step === 3 }">
								<a id="newClientWizard-t-2" href="#newClientWizard-h-2" @click.prevent>
									<span class="number">3</span>
									<span class="title">Profession Information</span>
								</a>
							</li>
							<li role="tab" class="disabled" :class="{ 'current': step === 4 }">
								<a id="newClientWizard-t-3" href="#newClientWizard-h-3" @click.prevent>
									<span class="number">4</span>
									<span class="title">Financial Information</span>
								</a>
							</li>
							<li role="tab" class="disabled last" :class="{ 'current': step === 5 }">
								<a id="newClientWizard-t-4" href="#newClientWizard-h-4" @click.prevent>
									<span class="number">5</span>
									<span class="title">Bank Accounts</span>
								</a>
							</li>
							<li role="tab" class="disabled last" :class="{ 'current': step === 6 }">
								<a id="newClientWizard-t-5" href="#newClientWizard-h-5" @click.prevent>
									<span class="number">6</span>
									<span class="title">Client Account</span>
								</a>
							</li>
						</ul>
					</div>
					<div class="content clearfix">
						<h3 class="mg-b-20 title" :class="{ 'current': step === 1 }" id="newClientWizard-h-0" tabindex="-1">Personal Information</h3>
						<section id="newClientWizard-p-0" role="tabpanel" aria-labelledby="newClientWizard-h-0" class="body" :class="{ 'd-none': step !== 1 }" aria-hidden="false">
							<div class="row">
								<div class="form-group col-md-6">
									<label>Title</label>
									<select v-model="form.salutation" class="form-control custom-select shadow-sm" :class="{ 'is-invalid': form.errors.has('salutation') }">
										<option selected :value="null" disabled>Select an option</option>
										<option v-for="option in options.salutations" :value="option">{{ option }}</option>
									</select>
									<has-error :form="form" field="salutation"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>First Name</label>
									<input v-model="form.first_name" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('first_name') }" placeholder="Enter client's first name" required>
									<has-error :form="form" field="first_name"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Surname</label>
									<input v-model="form.last_name" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('last_name') }" placeholder="Enter client's last name" required>
									<has-error :form="form" field="last_name"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Other Names</label>
									<input v-model="form.additional_names" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('additional_names') }" placeholder="Enter client's other names">
									<has-error :form="form" field="additional_names"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Date of Birth</label>
									<input v-model="form.birth_date" type="date" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('birth_date') }" placeholder="Enter client's birth date" required>
									<has-error :form="form" field="birth_date"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label class="d-block">Gender</label>
									<div class="py-2">
										<div class="custom-control custom-radio d-inline mr-2">
											<input v-model="form.gender" type="radio" id="male" value="M" class="custom-control-input">
											<label class="custom-control-label" for="male">Male</label>
										</div>
										<div class="custom-control custom-radio d-inline ml-2">
											<input v-model="form.gender" type="radio" id="female" value="F" class="custom-control-input">
											<label class="custom-control-label" for="female">Female</label>
										</div>
										<has-error :form="form" field="gender" :class="'d-block'"></has-error>
									</div>
								</div>
								<div class="form-group col-md-6">
									<label>Country</label>
									<country-picker-component :countries="options.countries" :country-selected="form.country" :country-iso-selected="form.country_iso" @update-country="updateCountry"></country-picker-component>
									<has-error :form="form" field="country"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>City</label>
									<city-picker-component :cities="options.cities" :country-selected="form.country_iso" :city-selected="form.city" @update-city="updateCity"></city-picker-component>
									<has-error :form="form" field="city"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Street Address</label>
									<input v-model="form.address_line_1" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('address_line_1') }" placeholder="Enter client's street address" required>
									<has-error :form="form" field="address_line_1"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>House Number</label>
									<input v-model="form.address_line_2" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('address_line_2') }" placeholder="Enter client's house number" required>
									<has-error :form="form" field="address_line_2"></has-error>
								</div>
								<div class="form-group col-md-12">
									<label>Postal Address</label>
									<input v-model="form.address_line_3" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('address_line_3') }" placeholder="Enter client's postal address" required>
									<has-error :form="form" field="address_line_3"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Email Address</label>
									<input v-model="form.email" type="email" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('email') }" placeholder="Enter client's email address" required>
									<has-error :form="form" field="email"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Phone Number</label>
									<phone-number-component :country="form.country" :phone-number="form.phone" @update-phone-country="updatePhoneCountry" @update-phone="updatePhone" :readonly="false"></phone-number-component>
									<has-error :form="form" field="phone"></has-error>
								</div>
							</div>
						</section>
						<h3 class="mg-b-20 title" :class="{ 'current': step === 2 }" id="newClientWizard-h-1" tabindex="-1">Identification</h3>
						<section id="newClientWizard-p-1" role="tabpanel" aria-labelledby="newClientWizard-h-1" class="body" :class="{ 'd-none': step !== 2 }" aria-hidden="true">
							<div class="row">
								<div class="form-group col-md-6">
									<label>Identification Type</label>
									<select v-model="form.identification_document_type" class="form-control custom-select shadow-sm" :class="{ 'is-invalid': form.errors.has('identification_document_type') }">
										<option selected :value="null" disabled>Select an option</option>
										<option v-for="option in options.identification_types" :value="option">{{ option }}</option>
									</select>
									<has-error :form="form" field="identification_document_type"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Document No</label>
									<input v-model="form.identification_document_number" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('identification_document_number') }" placeholder="Enter document no" required>
									<has-error :form="form" field="identification_document_number"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Expiry Date</label>
									<input v-model="form.identification_document_expiry_date" type="date" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('identification_document_expiry_date') }" placeholder="Enter expiry date" required>
									<has-error :form="form" field="identification_document_expiry_date"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Country of Issuance</label>
									<select v-model="form.identification_document_country" class="form-control custom-select shadow-sm" :class="{ 'is-invalid': form.errors.has('identification_document_country') }">
										<option selected :value="null" disabled>Select an option</option>
										<option v-for="option in options.countries" :value="option.name">{{ option.name }}</option>
									</select>
									<has-error :form="form" field="identification_document_country"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Country of Domicile</label>
									<select v-model="form.country_of_domicile" class="form-control custom-select shadow-sm" :class="{ 'is-invalid': form.errors.has('country_of_domicile') }">
										<option selected :value="null" disabled>Select an option</option>
										<option v-for="option in options.countries" :value="option.name">{{ option.name }}</option>
									</select>
									<has-error :form="form" field="country_of_domicile"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Nationality</label>
									<select v-model="form.nationality" class="form-control custom-select shadow-sm" :class="{ 'is-invalid': form.errors.has('nationality') }">
										<option selected :value="null" disabled>Select an option</option>
										<option v-for="option in options.countries" :value="option.nationality">{{ option.nationality }}</option>
									</select>
									<has-error :form="form" field="nationality"></has-error>
								</div>
								<div class="form-group col-12">
									<label>In Which Country Is Client Resident For Tax Purposes?</label>
									<select v-model="form.country_for_tax" class="form-control custom-select shadow-sm" :class="{ 'is-invalid': form.errors.has('country_for_tax') }">
										<option selected :value="null" disabled>Select an option</option>
										<option v-for="option in options.countries" :value="option.name">{{ option.name }}</option>
									</select>
									<has-error :form="form" field="country_for_tax"></has-error>
								</div>
								<div class="form-group col-12">
									<label>Is Client Resident In the US For Tax Purposes?</label>
									<div class="d-block">
										<div class="custom-control custom-radio d-inline mr-2">
											<input v-model="form.is_us_person" type="radio" id="is-us-person-yes" :value="1" class="custom-control-input" :class="{ 'is-invalid': form.errors.has('is_us_person') }">
											<label class="custom-control-label" for="is-us-person-yes">Yes</label>
										</div>
										<div class="custom-control custom-radio d-inline ml-2">
											<input v-model="form.is_us_person" type="radio" id="is-us-person-no" :value="0" class="custom-control-input" :class="{ 'is-invalid': form.errors.has('is_us_person') }">
											<label class="custom-control-label" for="is-us-person-no">No</label>
										</div>
									</div>
									<has-error :form="form" field="is_us_person" :class="'d-block'"></has-error>
								</div>
								<div class="form-group col-12">
									<label>Is Client Political Exposed Person</label>
									<div class="d-block mg-t-10">
										<div class="custom-control custom-radio d-inline mr-2">
											<input v-model="form.is_politically_exposed" type="radio" id="is-pep-yes" :value="1" class="custom-control-input" :class="{ 'is-invalid': form.errors.has('is_politically_exposed') }">
											<label class="custom-control-label" for="is-pep-yes">Yes</label>
										</div>
										<div class="custom-control custom-radio d-inline ml-2">
											<input v-model="form.is_politically_exposed" type="radio" id="is-pep-no" :value="0" class="custom-control-input" :class="{ 'is-invalid': form.errors.has('is_politically_exposed') }">
											<label class="custom-control-label" for="is-pep-no">No</label>
										</div>
									</div>
									<has-error :form="form" field="is_politically_exposed" :class="'d-block'"></has-error>
								</div>
							</div>
						</section>
						<h3 class="mg-b-20 title" :class="{ 'current': step === 3 }" id="newClientWizard-h-2" tabindex="-1">Profession Information</h3>
						<section id="newClientWizard-p-2" role="tabpanel" aria-labelledby="newClientWizard-h-2" class="body" :class="{ 'd-none': step !== 3 }" aria-hidden="true">
							<div class="row">
								<div class="form-group col-12">
									<label class="mb-2">Select your Position/Function</label>
									<div class="d-block">
										<div class="custom-control custom-radio d-inline mr-2">
											<input v-model="form.employment_type" type="radio" value="Self-Employed" id="self-employed" class="custom-control-input" :class="{ 'is-invalid': form.errors.has('employment_type') }" required>
											<label class="custom-control-label" for="self-employed">Self-Employed<span class="hide"> Person</span></label>
										</div>
										<div class="custom-control custom-radio d-inline ml-2">
											<input v-model="form.employment_type" type="radio" value="Employed" id="employed" class="custom-control-input" :class="{ 'is-invalid': form.errors.has('employment_type') }" required>
											<label class="custom-control-label" for="employed">Employed<span class="hide"> Person</span></label>
										</div>
									</div>
									<has-error :form="form" field="employment_type" :class="'d-block'"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Company Name</label>
									<input v-model="form.company_name" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('company_name') }" :placeholder="(form.employment_type === 'Employed') ? 'Enter employer company name': 'Enter client\'s company name'" required>
									<has-error :form="form" field="company_name"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Industry</label>
									<select v-model="form.area_of_activity" class="form-control custom-select shadow-sm" :class="{ 'is-invalid': form.errors.has('area_of_activity') }" required>
										<option selected :value="null" disabled>Select an option</option>
										<option v-for="option in options.professions" :value="option.id">{{ option.name }}</option>
									</select>
									<has-error :form="form" field="area_of_activity"></has-error>
								</div>
								<div class="form-group col-md-6" v-if="form.employment_type === 'Employed'">
									<label>Name of Profession</label>
									<input v-model="form.name_of_profession" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('name_of_profession') }" placeholder="Enter client's profession name" required>
									<has-error :form="form" field="name_of_profession"></has-error>
								</div>
								<div class="form-group col-md-6" v-if="form.employment_type === 'Employed'">
									<label>Your Function</label>
									<select v-model="form.function" class="form-control custom-select shadow-sm" :class="{ 'is-invalid': form.errors.has('function') }" required>
										<option selected :value="null" disabled>Select an option</option>
										<option v-for="option in options.functions" :value="option">{{ option }}</option>
									</select>
									<has-error :form="form" field="function"></has-error>
								</div>
							</div>
						</section>
						<h3 class="mg-b-20 title" :class="{ 'current': step === 4 }" id="newClientWizard-h-3" tabindex="-1">Financial Information</h3>
						<section id="newClientWizard-p-3" role="tabpanel" aria-labelledby="newClientWizard-h-3" class="body" :class="{ 'd-none': step !== 4 }" aria-hidden="true">
							<div class="row">
								<div class="form-group col-md-6">
									<label>Annual Income</label>
									<select v-model="form.annual_income" class="form-control custom-select shadow-sm" :class="{ 'is-invalid': form.errors.has('annual_income') }">
										<option selected :value="null" disabled>Select an option</option>
										<option v-for="option in options.annual_income" :value="option.id">{{ option.name }}</option>
									</select>
									<has-error :form="form" field="annual_income"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Total Assets</label>
									<select v-model="form.total_assets" class="form-control custom-select shadow-sm" :class="{ 'is-invalid': form.errors.has('total_assets') }">
										<option selected :value="null" disabled>Select an option</option>
										<option v-for="option in options.total_assets" :value="option.id">{{ option.name }}</option>
									</select>
									<has-error :form="form" field="total_assets"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Expected Amount of the First Deposit</label>
									<div class="input-group shadow-sm">
										<div class="input-group-prepend value">
											<span class="input-group-text">GHS</span>
										</div>
										<input v-model="form.expected_first_deposit" type="number" class="form-control" :class="{ 'is-invalid': form.errors.has('expected_first_deposit') }" placeholder="0.00" required>
									</div>
									<has-error :form="form" field="expected_first_deposit" :class="'d-block'"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Planned Investment in the First 12 months</label>
									<div class="input-group shadow-sm">
										<div class="input-group-prepend value">
											<span class="input-group-text">GHS</span>
										</div>
										<input v-model="form.planned_investment_amount" type="number" class="form-control" :class="{ 'is-invalid': form.errors.has('planned_investment_amount') }" placeholder="0.00" required>
									</div>
									<has-error :form="form" field="planned_investment_amount" :class="'d-block'"></has-error>
								</div>
								<div class="form-group col-md-12">
									<label>Source of Funds</label>
									<select v-model="form.source_of_funds" class="form-control custom-select shadow-sm ht-200" :class="{ 'is-invalid': form.errors.has('source_of_funds') }" multiple>
										<option v-for="option in options.source_of_funds" :value="option.id">{{ option.name }}</option>
									</select>
									<has-error :form="form" field="source_of_funds"></has-error>
								</div>
								<div class="form-group col-md-12">
									<label class="d-block tx-semibold">Beneficial Owners</label>
									<label class="d-block tx-color-03">
										The Beneficial Owner is defined as any individual who, 
										from an economic perspective, actually pays the deposit into the account.
									</label>
									<label class="d-block">Who is the Beneficial Owner of the assets managed in this account?</label>
									
									<div class="custom-control custom-radio d-block mr-3" v-for="(option, index) in options.beneficial_owner">
										<input v-model="form.beneficial_owner" type="radio" :id="index" :value="option.value" class="custom-control-input">
										<label class="custom-control-label" :for="index">{{ option.name }}</label>
									</div>

									<has-error :form="form" field="beneficial_owner"></has-error>
								</div>
							</div>
						</section>
						<h3 class="mg-b-20 title" :class="{ 'current': step === 5 }" id="newClientWizard-h-4" tabindex="-1">Bank Accounts</h3>
						<section id="newClientWizard-p-4" role="tabpanel" aria-labelledby="newClientWizard-h-4" class="body" :class="{ 'd-none': step !== 5 }" aria-hidden="true">
							<div class="row">
								<h5 class="tx-color-01 col-md-12">Bank Account Information for GHS</h5>
								<div class="form-group col-md-6">
									<label>Bank Name</label>
									<bank-selector-component :bank="form.ghs_bank" @change-bank="changeGhsBank"></bank-selector-component>
									<has-error :form="form" field="ghs_bank"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Branch Name</label>
									<bank-branch-selector-component :bank="form.ghs_bank" :branch="form.ghs_branch" @change-branch="changeGhsBankBranch"></bank-branch-selector-component>
									<has-error :form="form" field="ghs_branch"></has-error>
								</div>
								<div class="form-group col-md-12">
									<label>Account Number</label>
									<input v-model="form.ghs_account_number" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('ghs_account_number') }" placeholder="Enter client's account number" required>
									<has-error :form="form" field="ghs_account_number"></has-error>
								</div>

								<h5 class="tx-color-01 mg-t-30 col-md-12">Bank Account Information for USD</h5>
								<div class="form-group col-md-6">
									<label>Bank Name</label>
									<bank-selector-component :bank="form.usd_bank" @change-bank="changeUsdBank"></bank-selector-component>
									<has-error :form="form" field="usd_bank"></has-error>
								</div>
								<div class="form-group col-md-6">
									<label>Branch Name</label>
									<bank-branch-selector-component :bank="form.usd_bank" :branch="form.usd_branch" @change-branch="changeUsdBankBranch"></bank-branch-selector-component>
									<has-error :form="form" field="usd_bank"></has-error>
								</div>
								<div class="form-group col-md-12">
									<label>Account Number</label>
									<input v-model="form.usd_account_number" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('usd_account_number') }" placeholder="Enter client's account number" required>
									<has-error :form="form" field="usd_account_number"></has-error>
								</div>
							</div>
						</section>
						<h3 class="mg-b-20 title" :class="{ 'current': step === 6 }" id="newClientWizard-h-5" tabindex="-1">Client Account</h3>
						<section id="newClientWizard-p-5" role="tabpanel" aria-labelledby="newClientWizard-h-5" class="body" :class="{ 'd-none': step !== 6 }" aria-hidden="true">
							<div class="row">
								<div class="form-group col-md-12">
									<label>External Client ID</label>
									<input v-model="form.external_client_id" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('external_client_id') }" placeholder="Enter client's account number" required>
									<has-error :form="form" field="external_client_id"></has-error>
								</div>
								<div class="form-group col-md-12">
									<label>External Custody Account ID</label>
									<input v-model="form.external_custody_account_id" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('external_custody_account_id') }" placeholder="Enter client's account number" required>
									<has-error :form="form" field="external_custody_account_id"></has-error>
								</div>
								<div class="form-group col-md-12">
									<label>External Portfolio ID</label>
									<input v-model="form.external_portfolio_id" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('external_portfolio_id') }" placeholder="Enter client's account number" required>
									<has-error :form="form" field="external_portfolio_id"></has-error>
								</div>
							</div>
						</section>
					</div>
					<div class="actions clearfix">
						<ul role="menu" aria-label="Pagination">
							<li :class="{ 'disabled': step === 1 }" aria-disabled="true"><a href="#previous" class="shadow-sm" role="menuitem" @click.prevent="previous">Previous</a></li>
							<li v-show="step !== 6"><a href="#next" class="shadow-sm" role="menuitem" @click.prevent="next">Next</a></li>
							<li v-show="step === 6">
								<button-component :busy="form.busy" :label="'Create Client'" :label-loading="'Please wait...'" :button-class="'btn btn-default shadow-sm mg-b-5 mg-sm-b-0'"></button-component>
							</li>
						</ul>
					</div>
				</form>
			</div>
		</div>
	</div>
</template>
<script>
	export default {
		updated() {
			feather.replace();
		},
		data() {
			return {
				loading: false,
				error: null,
				options: null,
				step: 1,
				form: new Form({
					external_client_id: null,
					external_custody_account_id: null,
					external_portfolio_id: null,
					salutation: null,
					first_name: null,
					last_name: null,
					additional_names: null,
					birth_date: null,
					gender: null,
					country: "GH",
					country_iso: "GHA",
					city: null,
					address_line_1: null,
					address_line_2: null,
					address_line_3: null,
					email: null,
					phone: null,
					identification_document_number: null,
					identification_document_type: null,
					identification_document_expiry_date: null,
					identification_document_country: null,
					country_of_domicile: null,
					nationality: null,
					country_for_tax: null,
					is_us_person: null,
					is_politically_exposed: null,
					ghs_bank_account: null,
					ghs_bank: null,
					ghs_branch: null,
					ghs_account_number: null,
					usd_bank_account: null,
					usd_bank: null,
					usd_branch: null,
					usd_account_number: null,
					annual_income: null,
					total_assets: null,
					expected_first_deposit: null,
					planned_investment_amount: null,
					source_of_funds: [],
					beneficial_owner: null,
					employment_type: null,
					company_name: null,
					area_of_activity: null,
					name_of_profession: null,
					function: null,
				}),
			};
		},
		created() {
			this.fetchData();
		},
		methods: {
			updateCountry: function (country) {
				this.form.country = country.iso;
				this.form.country_iso = country.code;
			},
			updateCity: function (city) {
				this.form.city = city;
			},
			updatePhoneCountry(value) {
				this.form.country = value;
			},
			updatePhone(value) {
				this.form.phone = value;
			},
			changeGhsBank(bank) {
				this.form.ghs_bank = bank;
			},
			changeGhsBankBranch(branch) {
				this.form.ghs_branch = branch;
			},
			changeUsdBank(bank) {
				this.form.usd_bank = bank;
			},
			changeUsdBankBranch(branch) {
				this.form.usd_branch = branch;
			},
			fetchPersonalInformationOptions() {
				return axios.get("/api/options/personal-information");
			},
			fetchFinancialInformationOptions() {
				return axios.get("/api/options/financial-information");
			},
			fetchCountryOptions() {
				return axios.get("/api/options/countries");
			},
			fetchCityOptions() {
				return axios.get("/api/options/cities");
			},
			fetchData() {
				this.error = this.client = null;
				this.loading = true;

				axios
					.all([
						this.fetchPersonalInformationOptions(),
						this.fetchFinancialInformationOptions(),
						this.fetchCountryOptions(),
						this.fetchCityOptions(),
					])
					.then(
						axios.spread((personal, finance, countries, cities) => {
							this.options = {
								...personal.data.data.options,
								...finance.data.data.options,
								...countries.data.data,
								...cities.data.data,
								identification_types: this.$store.getters
									.identificationTypes,
								beneficial_owner: [
									{
										name: "Client",
										value: true,
									},
									{
										name: "Client and a Third Party",
										value: false,
									},
								],
							};
						})
					)
					.catch((error) => {
						this.error = error.toString();
					})
					.finally(() => {
						this.loading = false;
					});
			},
			previous() {
				if (this.step !== 1) {
					this.step--;
				}
			},
			next() {
				if (this.step !== 6) {
					this.step++;
				}
			},
			newClient() {
				this.form.post("/api/customers").then(({ data }) => {
					if (!this.form.any) {
						this.$router.push({
							name: "clients.show",
							params: { id: data.data.customer.external_client_id },
						});
					}
				});
			},
		},
	};
</script>